import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../lib/brand";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, fullName);
      toast.success("Welcome to Marianne.");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAF8]" data-testid="register-page">
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#2A0F5A] text-[#FAFAF8]">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={30} />
          <span className="font-serif text-2xl leading-none">Marianne</span>
        </Link>
        <div>
          <div className="label-eyebrow text-[#C5A059] mb-3">Join the tribunal</div>
          <h1 className="font-serif text-5xl tracking-tight leading-tight">
            Liberté.<br />Procédure.<br />Préparation.
          </h1>
          <p className="mt-6 text-[#FAFAF8]/70 max-w-md">
            Open your first case for free. Keep your evidence private, your record ordered, and
            your options open.
          </p>
        </div>
        <div className="text-xs text-[#FAFAF8]/50">© 2026 Mahakali Tribunal</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#4A5568] mb-10 hover:text-[#2A0F5A]">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="label-eyebrow text-[#C5A059] mb-3">Create account</div>
          <h2 className="font-serif text-4xl font-medium mb-8">Take a seat.</h2>
          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label className="label-eyebrow mb-2 block">Full name</Label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="rounded-none border-[#E8E3F0] focus-visible:ring-0 focus-visible:border-[#2A0F5A] bg-white"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 block">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none border-[#E8E3F0] focus-visible:ring-0 focus-visible:border-[#2A0F5A] bg-white"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 block">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-none border-[#E8E3F0] focus-visible:ring-0 focus-visible:border-[#2A0F5A] bg-white"
                data-testid="register-password-input"
              />
              <div className="text-xs text-[#4A5568] mt-1.5">At least 6 characters.</div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <div className="mt-8 text-sm text-[#4A5568]">
            Already a member?{" "}
            <Link to="/login" className="text-[#2A0F5A] underline underline-offset-4 hover:text-[#C5A059]" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
